import type { TripLeg } from "@poi-planner/shared";
import { daysBetweenDates } from "./zonedTime";

/**
 * One date's slice of a leg, shaped like `AccommodationStaySegment` so the
 * month view can treat a leg as a third consumer of the same range-across-week-
 * rows machinery. The difference is where the range comes from: an
 * accommodation stay is derived by scanning consecutive `sleep` entries, while
 * a leg carries an explicit inclusive `startDate`/`endDate`.
 *
 * `index`/`count` and `isStart`/`isEnd` describe the leg's whole range, not the
 * visible window: a leg continuing past the edge of the rendered dates still
 * reports `isStart: false` there, so the chip renders a continuation marker and
 * withholds the resize handle for an edge that is not on screen.
 */
export interface LegSegment {
  date: string;
  legId: string;
  index: number;
  count: number;
  isStart: boolean;
  isEnd: boolean;
  /** The leg continues into the previous rendered row. */
  clippedTop: boolean;
  /** The leg continues into the next rendered row. */
  clippedBottom: boolean;
}

/**
 * The segment of `leg` covering `date`, or null when the leg does not span it.
 * `rowStartDate`/`rowEndDate` bound the row the date is rendered in (a month
 * week row), which is what decides clipping.
 */
export function legSegmentForDate(
  leg: TripLeg,
  date: string,
  rowStartDate: string,
  rowEndDate: string,
): LegSegment | null {
  if (date < leg.startDate || date > leg.endDate) return null;
  const index = daysBetweenDates(leg.startDate, date);
  const count = daysBetweenDates(leg.startDate, leg.endDate) + 1;
  return {
    date,
    legId: leg.id,
    index,
    count,
    isStart: index === 0,
    isEnd: index === count - 1,
    clippedTop: leg.startDate < rowStartDate,
    clippedBottom: leg.endDate > rowEndDate,
  };
}

/**
 * Assign each leg the first lane whose previous leg ends before this one
 * starts. Non-overlapping legs therefore share a row, and only genuine
 * overlaps — a hotel-to-hotel handoff day — take a second one.
 */
export function packLegLanes(legs: TripLeg[]): Map<string, number> {
  const laneEnds: string[] = [];
  const lanes = new Map<string, number>();
  for (const leg of [...legs].sort(
    (a, b) =>
      a.startDate.localeCompare(b.startDate) || a.id.localeCompare(b.id),
  )) {
    let lane = laneEnds.findIndex((end) => end < leg.startDate);
    if (lane === -1) lane = laneEnds.length;
    laneEnds[lane] = leg.endDate;
    lanes.set(leg.id, lane);
  }
  return lanes;
}
